import { Link } from "react-router-dom";
import Logo from "../icons/logoColor.svg?react";
import { WhitePage } from "./util";

export const Over = () => (
  <WhitePage>
    <div>
      <h2 className="text-3xl font-bold inline-block">Bodematlas</h2>
      <span className="pl-4 mb-10 text-gray-500">Over bodematlas</span>
    </div>

    <section className="relative mx-auto max-w-6xl px-4 py-4 sm:py-8">
      <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-white/70 px-3 py-1 text-xs font-medium text-emerald-800 ring-1 ring-emerald-100">
            <span className="h-2 w-2 rounded-full bg-emerald-600"></span>
            Bodematlas B.V. – perceelinzicht in minuten
          </div>

          <h1 className="mt-5 text-3xl font-extrabold tracking-tight sm:text-5xl">
            Bodematlas verzamelt, verbindt, vernieuwt.
          </h1>

          <p className="mt-4 text-lg leading-relaxed text-slate-700">
            Bodematlas bundelt perceelsgebonden informatie in één praktisch overzicht. Wij
            verzamelen relevante data, verbinden databronnen en betrokken partijen, en vernieuwen de
            manier waarop bodeminformatie wordt toegepast in ruimtelijke ontwikkeling.
          </p>

          <p className="mt-3 text-slate-700">
            Wij geven inzicht aan iedereen die iets te maken heeft met de bodem.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/location/5.94191,52.22936"
              className="inline-flex items-center justify-center rounded-lg bg-emerald-700 px-5 py-3 text-sm font-semibold text-white hover:bg-emerald-800"
            >
              Start kavelanalyse
            </Link>
            <a
              href="#wat-is"
              className="inline-flex items-center justify-center rounded-lg border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-900 hover:bg-white"
            >
              Lees meer
            </a>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-xs text-slate-500">Output</div>
              <div className="mt-1 text-sm font-semibold">Integraal locatierapport</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-xs text-slate-500">Focus</div>
              <div className="mt-1 text-sm font-semibold">Risico’s & kansen</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-xs text-slate-500">Werkwijze</div>
              <div className="mt-1 text-sm font-semibold">Themakaarten per perceel</div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-emerald-100 bg-white p-6 shadow-sm">
          <h2 className="text-base font-semibold">Wat krijg je in één oogopslag?</h2>
          <p className="mt-2 text-sm text-slate-600">
            Een overzicht dat juridische, ruimtelijke, milieutechnische en marktinformatie
            samenbrengt.
          </p>

          <div className="mt-5 space-y-3">
            <div className="flex gap-3">
              <Logo className="mt-0.5 w-5 h-5" />
              <div>
                <div className="text-sm font-semibold">Kadastrale & ruimtelijke informatie</div>
                <div className="text-sm text-slate-600">
                  Adres, perceel, bestemming, gebruik, oppervlakte.
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Logo className="mt-0.5 w-5 h-5" />
              <div>
                <div className="text-sm font-semibold">Omgevingsaspecten</div>
                <div className="text-sm text-slate-600">
                  Bereikbaarheid, voorzieningen, veiligheid, gebiedsprofiel.
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Logo className="mt-0.5 w-5 h-5" />
              <div>
                <div className="text-sm font-semibold">Milieu, ecologie & bodem</div>
                <div className="text-sm text-slate-600">
                  Bodemkwaliteit, geluid, lucht, flora/fauna, archeologie.
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Logo className="mt-0.5 w-5 h-5" />
              <div>
                <div className="text-sm font-semibold">Ontwikkeling</div>
                <div className="text-sm text-slate-600">
                  Eigendom, erfpacht, jurisdische beperkingen, voorkeursrecht.
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Logo className="mt-0.5 w-5 h-5" />
              <div>
                <div className="text-sm font-semibold">Markt & ontwikkelpotentie</div>
                <div className="text-sm text-slate-600">
                  Doelgroep, marktpositie, beleid, ontwikkelkansen.
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-xl bg-emerald-50 p-4">
            <div className="text-sm font-semibold text-emerald-900">
              Onderbouwde basis voor besluitvorming
            </div>
            <div className="mt-1 text-sm text-emerald-900/80">
              Minder zoeken, minder interpretatie, sneller zekerheid.
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="wat-is" className="mx-auto max-w-6xl px-4 py-14">
      <div className="max-w-3xl">
        <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">Wat is Bodematlas?</h2>
        <p className="mt-4 text-slate-700 leading-relaxed">
          Bodematlas is een digitaal analyseplatform voor kadastrale percelen in Nederland. Het
          platform bestaat uit een samenhangende verzameling van{" "}
          <span className="font-semibold">themakaarten</span> met perceelsgebonden informatie. Alle
          relevante data wordt gebundeld in één helder en praktisch overzicht.
        </p>
        <p className="mt-4 text-slate-700 leading-relaxed">
          De informatie is zorgvuldig samengesteld uit algemene publieke bronnen,
          overheidsdatabases, specialistische databanken en bedrijfsgegevens. In plaats van losse
          downloads en versnipperde datasets biedt Bodematlas één integraal locatierapport.
        </p>
      </div>

      <div className="mt-10 grid gap-6 md:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 p-6">
          <div className="text-sm font-semibold">Verzamelt</div>
          <p className="mt-2 text-sm text-slate-600">
            Relevante perceelsdata uit meerdere bronnen, gestructureerd per thema.
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 p-6">
          <div className="text-sm font-semibold">Verbindt</div>
          <p className="mt-2 text-sm text-slate-600">
            Databronnen, kaarten en inzichten tot één samenhangend beeld van een locatie.
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 p-6">
          <div className="text-sm font-semibold">Vernieuwt</div>
          <p className="mt-2 text-sm text-slate-600">
            Toepassing van bodeminformatie: sneller van data naar besluitvorming.
          </p>
        </div>
      </div>
    </section>

    <section id="hoe-werkt" className="bg-slate-50 border-y border-slate-200">
      <div className="mx-auto max-w-6xl px-4 py-14">
        <div className="max-w-3xl">
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
            Hoe werkt de applicatie?
          </h2>
          <p className="mt-4 text-slate-700 leading-relaxed">
            In een paar stappen van perceelselectie naar integraal locatierapport.
          </p>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          <div className="rounded-2xl bg-white border border-slate-200 p-6">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-emerald-700 text-white flex items-center justify-center text-sm font-bold">
                1
              </div>
              <div className="text-sm font-semibold">Selecteer een perceel</div>
            </div>
            <p className="mt-3 text-sm text-slate-600">
              Filter op o.a. oppervlakte, gemeente, bestemming/gebruik, eigendom of erfpacht,
              bodemkwaliteit, geluidsbelasting, marktpotentie en gemeentelijk draagvlak.
            </p>
          </div>

          <div className="rounded-2xl bg-white border border-slate-200 p-6">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-emerald-700 text-white flex items-center justify-center text-sm font-bold">
                2
              </div>
              <div className="text-sm font-semibold">Thematische analyse</div>
            </div>
            <p className="mt-3 text-sm text-slate-600">
              Bodematlas genereert een overzicht per thema, gekoppeld aan perceelsniveau. Hierdoor
              is de informatie direct relevant voor het betreffende kavel.
            </p>
          </div>

          <div className="rounded-2xl bg-white border border-slate-200 p-6">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-emerald-700 text-white flex items-center justify-center text-sm font-bold">
                3
              </div>
              <div className="text-sm font-semibold">Integrale rapportage</div>
            </div>
            <p className="mt-3 text-sm text-slate-600">
              Alle data wordt gebundeld met een samenvattende beoordeling en concrete
              vervolgstappen, zodat je sneller en zekerder kunt beslissen.
            </p>
          </div>
        </div>

        <div className="mt-10 rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
          <div className="text-sm font-semibold text-emerald-900">Waarom dit werkt</div>
          <p className="mt-2 text-sm text-emerald-900/80">
            In ruimtelijke ontwikkeling is informatie vaak verspreid over meerdere loketten, kaarten
            en documenten. Bodematlas brengt het samen, structureert het en maakt risico’s en kansen
            vroegtijdig inzichtelijk.
          </p>
        </div>
      </div>
    </section>

    <section id="voor-wie" className="mx-auto max-w-6xl px-4 py-14">
      <div className="max-w-3xl">
        <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">Voor wie is Bodematlas?</h2>
        <p className="mt-4 text-slate-700 leading-relaxed">
          Bodematlas is ontwikkeld voor partijen die professioneel met grond en ontwikkeling werken.
          Iedereen die investeert in grond, bouwt op ondergrond.
        </p>
      </div>

      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 p-5 text-sm font-semibold">
          Projectontwikkelaars
        </div>
        <div className="rounded-2xl border border-slate-200 p-5 text-sm font-semibold">
          Vastgoedbeleggers
        </div>
        <div className="rounded-2xl border border-slate-200 p-5 text-sm font-semibold">
          Gemeenten
        </div>
        <div className="rounded-2xl border border-slate-200 p-5 text-sm font-semibold">
          Adviesbureaus
        </div>
        <div className="rounded-2xl border border-slate-200 p-5 text-sm font-semibold">
          Grondeigenaren
        </div>
        <div className="rounded-2xl border border-slate-200 p-5 text-sm font-semibold">
          Agrarische ondernemers
        </div>
      </div>
    </section>

    <section id="over" className="bg-white border-t border-slate-200">
      <div className="mx-auto max-w-6xl px-4 py-14">
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">Over Bodematlas B.V.</h2>
            <p className="mt-4 text-slate-700 leading-relaxed">
              Bodematlas B.V. is opgericht vanuit de overtuiging dat bodeminformatie toegankelijker,
              gestructureerder en praktischer moet zijn.
            </p>

            <div className="mt-6 space-y-2 text-slate-700">
              <div className="flex gap-2">
                <span className="mt-1 inline-block h-2 w-2 rounded-full bg-emerald-700"></span>
                <span>Wij verzamelen relevante informatie.</span>
              </div>
              <div className="flex gap-2">
                <span className="mt-1 inline-block h-2 w-2 rounded-full bg-emerald-700"></span>
                <span>Wij verbinden databronnen en partijen.</span>
              </div>
              <div className="flex gap-2">
                <span className="mt-1 inline-block h-2 w-2 rounded-full bg-emerald-700"></span>
                <span>Wij vernieuwen de toepassing van bodeminformatie.</span>
              </div>
            </div>

            <p className="mt-6 text-slate-700 leading-relaxed">
              Door informatie te bundelen en te duiden ontstaat overzicht. Door overzicht ontstaat
              betere besluitvorming.
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
            <h3 className="text-base font-semibold">Begin vandaag nog</h3>
            <p className="mt-2 text-sm text-slate-600">
              Maak de stap van losse kaarten naar een integraal locatierapport.
            </p>

            <div className="mt-6 flex flex-col gap-3">
              <Link
                to="/location/5.94191,52.22936"
                className="inline-flex items-center justify-center rounded-lg bg-emerald-700 px-5 py-3 text-sm font-semibold text-white hover:bg-emerald-800"
              >
                Start kavelanalyse
              </Link>
              <a
                href="/report/rapport2"
                className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-50"
              >
                Bekijk voorbeeldrapport
              </a>
              <Link
                to="/login"
                className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-50"
              >
                Maak een account of log in
              </Link>
              <a
                href="mailto:info@bodematlas.nl"
                className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-50"
              >
                Neem contact op
              </a>
            </div>

            <div className="mt-6 rounded-xl bg-white p-4 text-sm text-slate-600 border border-slate-200">
              <div className="font-semibold text-slate-900">Info</div>
              <div className="mt-1">
                <div>Bodematlas BV</div>
                <div>KVK 97467928</div>
                <div>
                  <a className="text-blue" href="mailto:info@bodematlas.nl">
                    info@bodematlas.nl
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </WhitePage>
);
